/**
 * Dual-handle In/Out slider for tweaking a timeline item's start/end inside its
 * source segment (§6.5.3). Precision = 0.1 second.
 *
 * Implementation: two native range inputs stacked; the second one z-indexes over
 * the first so both thumbs are draggable. The visible "filled" track is drawn
 * by a colored div absolute-positioned in between.
 */
import { useId } from 'react'

interface Props {
  segmentDuration: number
  inSeconds: number
  outSeconds: number
  onChange: (next: { in: number; out: number }) => void
  disabled?: boolean
}

const STEP = 0.1
const MIN_CLIP = 0.5  // 至少保留半秒长度

function clamp(v: number, lo: number, hi: number) {
  return Math.min(hi, Math.max(lo, v))
}

export function InOutSlider({
  segmentDuration,
  inSeconds,
  outSeconds,
  onChange,
  disabled = false,
}: Props) {
  const id = useId()
  const pctIn = segmentDuration > 0 ? (inSeconds / segmentDuration) * 100 : 0
  const pctOut = segmentDuration > 0 ? (outSeconds / segmentDuration) * 100 : 100

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <label className="flex items-center gap-1">
          In
          <input
            type="number"
            min={0}
            max={segmentDuration}
            step={STEP}
            value={Number(inSeconds.toFixed(1))}
            disabled={disabled}
            onChange={(e) => {
              const next = clamp(parseFloat(e.target.value || '0'), 0, outSeconds - MIN_CLIP)
              if (!Number.isNaN(next)) onChange({ in: next, out: outSeconds })
            }}
            className="w-16 px-1 py-0.5 border rounded bg-background text-right font-mono disabled:opacity-50"
          />s
        </label>
        <span>Length: <b className="font-mono">{(outSeconds - inSeconds).toFixed(1)}s</b></span>
        <label className="flex items-center gap-1">
          Out
          <input
            type="number"
            min={0}
            max={segmentDuration}
            step={STEP}
            value={Number(outSeconds.toFixed(1))}
            disabled={disabled}
            onChange={(e) => {
              const next = clamp(parseFloat(e.target.value || '0'), inSeconds + MIN_CLIP, segmentDuration)
              if (!Number.isNaN(next)) onChange({ in: inSeconds, out: next })
            }}
            className="w-16 px-1 py-0.5 border rounded bg-background text-right font-mono disabled:opacity-50"
          />s
        </label>
      </div>

      {/* [FIX-2026-06-08] Two stacked range inputs would let the top (Out) input
          swallow all pointer events, so the In (left) handle couldn't be
          dragged. Make the inputs click-through except on their thumbs. */}
      <style>{`
        .inout-range { pointer-events: none; -webkit-appearance: none; appearance: none; background: transparent; }
        .inout-range::-webkit-slider-thumb { pointer-events: auto; -webkit-appearance: none; appearance: none; width: 16px; height: 16px; border-radius: 9999px; cursor: pointer; background: transparent; }
        .inout-range::-moz-range-thumb { pointer-events: auto; width: 16px; height: 16px; border: 0; border-radius: 9999px; cursor: pointer; background: transparent; }
      `}</style>
      <div className="relative h-8">
        {/* base track */}
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1.5 bg-muted rounded-full" />
        {/* filled segment */}
        <div
          className="absolute top-1/2 -translate-y-1/2 h-1.5 bg-spectrum-magenta rounded-full"
          style={{ left: `${pctIn}%`, width: `${Math.max(0, pctOut - pctIn)}%` }}
        />
        {/* In handle (range input) */}
        <input
          id={`${id}-in`}
          type="range"
          min={0}
          max={segmentDuration}
          step={STEP}
          value={inSeconds}
          disabled={disabled}
          onChange={(e) => {
            const next = clamp(
              parseFloat(e.target.value),
              0,
              outSeconds - MIN_CLIP,
            )
            onChange({ in: next, out: outSeconds })
          }}
          className="inout-range absolute inset-0 w-full opacity-0"
          style={{ zIndex: 1 }}
        />
        {/* Out handle */}
        <input
          id={`${id}-out`}
          type="range"
          min={0}
          max={segmentDuration}
          step={STEP}
          value={outSeconds}
          disabled={disabled}
          onChange={(e) => {
            const next = clamp(
              parseFloat(e.target.value),
              inSeconds + MIN_CLIP,
              segmentDuration,
            )
            onChange({ in: inSeconds, out: next })
          }}
          className="inout-range absolute inset-0 w-full opacity-0"
          style={{ zIndex: 2 }}
        />
        {/* visual handles */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 bg-spectrum-magenta rounded-full border-2 border-background shadow"
          style={{ left: `${pctIn}%` }}
          aria-hidden
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 bg-spectrum-magenta rounded-full border-2 border-background shadow"
          style={{ left: `${pctOut}%` }}
          aria-hidden
        />
      </div>
    </div>
  )
}
